import { create } from 'zustand';

export interface BannerRow {
	row?: number;
	qr?: string;
	reason: string;
}

export interface BannerDetailGroup {
	label: string;
	rows: BannerRow[];
}

export interface BannerError {
	title: string;
	summary: string;
	details?: BannerDetailGroup[];
}

interface ErrorBannerStore {
	current: BannerError | null;
}

export const useErrorBannerStore = create<ErrorBannerStore>(() => ({ current: null }));

export function showError(e: BannerError): void {
	useErrorBannerStore.setState({ current: e });
}

export function clearError(): void {
	useErrorBannerStore.setState({ current: null });
}

interface ApiErrorLike {
	status?: number;
	body?: Record<string, unknown>;
	message?: string;
}

/**
 * Turn a thrown API error (from lib/api.req) into a banner. Handles the
 * standardized error shapes (validation_failed, project_change_not_allowed,
 * project_locked_by_pallet) and falls back to a generic message.
 */
export function showApiError(title: string, err: unknown): void {
	const e = err as ApiErrorLike;
	const body = (e?.body ?? {}) as Record<string, unknown>;
	const code = typeof body.error === 'string' ? body.error : null;
	const message = typeof body.message === 'string' ? body.message : e?.message || 'Request failed';

	if (code === 'validation_failed' && Array.isArray(body.invalidRows)) {
		const rows = (body.invalidRows as Array<{ row: number; qr?: string; missing: string[] }>).map((r) => ({
			row: r.row,
			qr: r.qr,
			reason: `Missing: ${r.missing.join(', ')}`,
		}));
		showError({
			title,
			summary: message,
			details: [{ label: `${rows.length} invalid row(s)`, rows }],
		});
		return;
	}

	if (code === 'duplicate_qr_in_file' && Array.isArray(body.duplicateRows)) {
		const rows = (body.duplicateRows as Array<{ row: number; qr: string; firstRow: number }>).map((r) => ({
			row: r.row,
			qr: r.qr,
			reason: `Duplicate of row ${r.firstRow}`,
		}));
		showError({
			title,
			summary: message,
			details: [{ label: `${rows.length} duplicate row(s)`, rows }],
		});
		return;
	}

	if (code === 'project_change_not_allowed' && Array.isArray(body.projectConflicts)) {
		const rows = (body.projectConflicts as Array<{
			row: number; qr: string; existing: string; attempted: string; palletCode?: string;
		}>).map((r) => ({
			row: r.row,
			qr: r.qr,
			reason: r.palletCode
				? `On pallet ${r.palletCode} (project "${r.existing}") — cannot change to "${r.attempted}"`
				: `Existing project "${r.existing}" cannot be changed to "${r.attempted}"`,
		}));
		showError({
			title,
			summary: message,
			details: [{ label: `${rows.length} project conflict(s)`, rows }],
		});
		return;
	}

	if (code === 'project_locked_by_pallet') {
		showError({ title, summary: message });
		return;
	}

	showError({
		title,
		summary: code ? `${code}: ${message}` : message,
	});
}
